import React, { useRef, useState } from 'react';
import Modal from '../ui/Modal';
import { RiMailLine, RiCheckDoubleLine, RiFileAddLine, RiFileTextLine } from 'react-icons/ri';

interface DocumentStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data: any) => void | Promise<void>;
  action: 'generate' | 'send' | 'sign' | 'complete';
  contractNumber?: string;
}

const DocumentStatusModal: React.FC<DocumentStatusModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  contractNumber
}) => {
  const [notes, setNotes] = useState('');
  const [signedBy, setSignedBy] = useState('');
  // BUG-S2074-005: ปุ่มยืนยันไม่มีกันกดซ้ำ → ดับเบิลคลิก = POST 2 ครั้ง (สร้างเอกสารได้ log 2 แถว ·
  // ส่ง/เซ็น/เสร็จสมบูรณ์ ครั้งที่ 2 ชน 400 แล้วขึ้น error ซ้อนข้อความสำเร็จ) · ref กันก่อน re-render
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  // sign action: backend requires a non-empty signer name (signed_by)
  const signedByMissing = action === 'sign' && !signedBy.trim();

  const handleSubmit = async () => {
    if (signedByMissing || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    let data: any = { notes };

    if (action === 'send') {
      data.email = ''; // ส่งค่าว่างเสมอ
    } else if (action === 'sign') {
      data.signed_by = signedBy.trim();
    }

    try {
      await onConfirm(data);
      // Reset form
      setNotes('');
      setSignedBy('');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (action) {
      case 'generate':
        return 'สร้างเอกสาร PDF';
      case 'send':
        return 'ส่งเอกสารให้ลูกค้า';
      case 'sign':
        return 'บันทึกการเซ็นเอกสาร';
      case 'complete':
        return 'เอกสารเสร็จสมบูรณ์';
      default:
        return '';
    }
  };

  const getIcon = () => {
    switch (action) {
      case 'generate':
        return <RiFileAddLine className="w-5 h-5 text-info-600" />;
      case 'send':
        return <RiMailLine className="w-5 h-5 text-warning-600" />;
      case 'sign':
        return <RiCheckDoubleLine className="w-5 h-5 text-success-600" />;
      case 'complete':
        return <RiFileTextLine className="w-5 h-5 text-accent-600" />;
      default:
        return null;
    }
  };

  const getActionMessage = () => {
    switch (action) {
      case 'generate':
        return 'ระบบจะสร้างเอกสาร PDF และเปลี่ยนสถานะเป็น "สร้าง PDF แล้ว"';
      case 'send':
        return 'ระบบจะบันทึกว่าส่งเอกสารให้ลูกค้าแล้ว';
      case 'sign':
        return 'ระบบจะบันทึกว่าลูกค้าเซ็นเอกสารแล้ว';
      case 'complete':
        return 'เอกสารจะถูกทำเครื่องหมายว่าเสร็จสมบูรณ์และไม่สามารถแก้ไขได้อีก';
      default:
        return '';
    }
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} title={getTitle()} size="sm">
      <div className="space-y-4">
        {/* Contract Info */}
        <div className="flex items-center space-x-3 bg-secondary-50 p-3 rounded-lg">
          {getIcon()}
          <div className="flex-1">
            <p className="text-xs text-secondary-500">สัญญาเลขที่</p>
            <p className="font-medium text-sm text-secondary-900">{contractNumber}</p>
          </div>
        </div>

        {/* Action Message */}
        {action === 'generate' && (
          <div className="bg-info-50 border border-info-200 rounded-md p-3">
            <p className="text-sm text-info-700">
              {getActionMessage()}
            </p>
          </div>
        )}

        {action === 'send' && (
          <div className="bg-warning-50 border border-warning-200 rounded-md p-3">
            <p className="text-sm text-warning-700">
              {getActionMessage()}
            </p>
          </div>
        )}

        {action === 'sign' && (
          <div className="bg-success-50 border border-success-200 rounded-md p-3">
            <p className="text-sm text-success-700">
              {getActionMessage()}
            </p>
          </div>
        )}

        {action === 'complete' && (
          <div className="bg-accent-50 border border-accent-200 rounded-md p-3">
            <p className="text-sm text-accent-700">
              {getActionMessage()}
            </p>
          </div>
        )}

        {/* Signed-by Field (sign action only — required by backend) */}
        {action === 'sign' && (
          <div>
            <label className="block text-sm font-medium text-secondary-700 mb-1">
              ชื่อผู้เซ็น <span className="text-danger-600">*</span>
            </label>
            <input
              type="text"
              aria-label="ชื่อผู้เซ็น"
              value={signedBy}
              onChange={(e) => setSignedBy(e.target.value)}
              className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
              placeholder="ระบุชื่อผู้ลงนามของลูกค้า"
              aria-invalid={signedByMissing}
              aria-describedby={signedByMissing ? 'signedBy-error' : undefined}
            />
            {signedByMissing && (
              <p id="signedBy-error" className="text-xs text-danger-600 mt-1">กรุณาระบุชื่อผู้เซ็น</p>
            )}
          </div>
        )}

        {/* Notes Field */}
        <div>
          <label className="block text-sm font-medium text-secondary-700 mb-1">
            หมายเหตุ
          </label>
          <textarea
            aria-label="หมายเหตุ"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            rows={3}
            placeholder="บันทึกเพิ่มเติม (ถ้ามี)"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-secondary-700 bg-secondary-100 hover:bg-secondary-200 rounded-md transition-colors"
          >
            ยกเลิก
          </button>
          <button
            onClick={handleSubmit}
            disabled={signedByMissing || submitting}
            className="px-4 py-2 text-sm text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:bg-secondary-400 disabled:cursor-not-allowed"
          >
            {submitting ? 'กำลังบันทึก...' : 'ยืนยัน'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DocumentStatusModal;